import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { UntypedFormControl } from '@angular/forms';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, concatMap, map, startWith, switchMap, toArray } from 'rxjs/operators';
import { StoragePlace } from '../storage-place/storage-place';
import { StoragePlaceService } from '../storage-place/storage-place.service';
import { StockService } from '../stock/stock.service';
import { BestellungExtern } from '../../wa-wi/bestellung-extern/bestellung-extern';
import { BestellungExternService } from '../../wa-wi/bestellung-extern/bestellung-extern.service';
import { Material, isEinkaufLaborArticle, isVerbrauchArticle } from '../material/material';
import { MaterialService } from '../material/material.service';
import { GoodReceipt } from './good-receipt';
import {
    ArticleScanAutocompleteComponent,
    ArticleScanSelectedEvent,
} from '../../components/article-scan-autocomplete/article-scan-autocomplete.component';
import { MatDialog } from '@angular/material/dialog';
import { GoodsReceiptAddArticleComponent } from './goods-receipt-add-article/goods-receipt-add-article.component';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneUtils } from '../../../../projects/kiene-core/src/lib/utils/KieneUtils';
import { GemeinkostenInvoiceKiService } from '../../wa-wi/gemeinkosten/gemeinkosten-invoice-review/gemeinkosten-invoice-ki.service';
import { WawiInvoiceDraftDto } from '../../wa-wi/gemeinkosten/gemeinkosten-invoice-review/gemeinkosten-invoice.models';
import { KieneBackendApiService } from '../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { LocalStorageService } from '../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';

const OPEN_ORDER_STATUS = [23, 24, 25, 27, 29];
/** bestand_insert_v2 verlangt ein MHD; chargenfrei (z. B. Kanülen) hat keins. */
const NO_EXPIRY = '2099-01-01';

export interface ScanLineOrderOption {
    bestellung_extern_id: number;
    bestell_nr: number;
    mengeSoll: number | null;
}

interface OrderPositionRef {
    bestellung_extern_id: number;
    bestell_nr: number;
    artikel_id: number;
    artikel: string;
    menge_soll: number;
    rest: number;
    chargenpflicht: unknown;
}

interface OrderProgressPosition {
    artikel: string;
    artikelId: number;
    soll: number;
    ist: number;
    we: number;
    offen: number;
    state: 'open' | 'partial' | 'done' | 'over' | 'complete';
}

interface OrderProgressView {
    bestellungExternId: number;
    bestellNr: number;
    lieferant: string;
    bestellt: Date | string | null;
    qtyPercent: number;
    over: boolean;
    hitCount: number;
    positions: OrderProgressPosition[];
}

export interface WareneingangScanLine {
    artikelId: number;
    artikel: string;
    menge: number;
    mengeSoll: number | null;
    charge: string;
    mhd: Date | null;
    included: boolean;
    matched: boolean;
    chargenpflichtig: boolean;
    bestellungExternId: number | null;
    bestellNr: number | null;
    orderOptions: ScanLineOrderOption[];
    gehtZurueck: boolean;
    /** true = Praxisverbrauch, false = Umsatz, null = noch nicht aus Artikel geladen */
    verbrauch: boolean | null;
    lagerplatz: StoragePlace | null;
    lagerplatzSuggestions: StoragePlace[];
}

@Component({
    selector: 'kiene-goods-receipt',
    templateUrl: './goods-receipt.component.html',
    styleUrls: ['./goods-receipt.component.scss'],
})
export class GoodsReceiptComponent implements AfterViewInit, OnDestroy {
    storagePlaceFormCtrl: UntypedFormControl;
    materialien: Observable<Material[]>;
    storagePlaces: Observable<StoragePlace[]>;
    possibleBestellungen: BestellungExtern[] = [];

    newMaterial: Material = new Material();
    goodReceipt: GoodReceipt = new GoodReceipt();
    storagePlaceSuggestions: StoragePlace[] = [];
    selectedStoragePlace: StoragePlace = new StoragePlace();
    showStoragePlaceAutocomplete = false;

    selectedOrder: BestellungExtern = new BestellungExtern();

    saveButtonDisabled = false;
    produktkennzeichnung: string;
    manualVerbrauch: boolean | null = null;

    pdfPanelOpen = false;
    scanFile: File | null = null;
    scanning = false;
    scanFailed = false;
    scanError = '';
    scanSaving = false;
    draft: WawiInvoiceDraftDto = null;
    scanKind = 'lieferschein';
    scanKindLocked = false;
    scanLines: WareneingangScanLine[] = [];
    scanOrders: BestellungExtern[] = [];
    scanSelectedOrders: BestellungExtern[] = [];
    ocrHadNoLines = false;
    scanLieferant: { lieferant_id: number; bezeichnung: string } = null;
    pdfSrc: Uint8Array | null = null;
    pdfObjectUrl: string | null = null;
    pdfPage = 1;
    pdfPages = 0;
    pdfLoading = false;
    scanInfoOpen = false;
    private selectedOrdersLoadSeq = 0;

    scanOrdersLoading = false;
    laborWareneingang = false;

    @ViewChild('materialAutocomplete', { static: false })
    materialAutocomplete: ArticleScanAutocompleteComponent;
    @ViewChild('pdfPicker') pdfPicker: ElementRef<HTMLInputElement>;

    constructor(
        private readonly bestellungExternService: BestellungExternService,
        private readonly storagePlaceService: StoragePlaceService,
        private readonly stockService: StockService,
        private readonly notification: MessageService,
        private readonly dialog: MatDialog,
        private readonly invoiceKi: GemeinkostenInvoiceKiService,
        private readonly api: KieneBackendApiService,
        private readonly clipboard: Clipboard,
        private readonly materialService: MaterialService,
        private readonly localStorageService: LocalStorageService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {
        this.laborWareneingang =
            this.localStorageService.hasPermission(1082) &&
            !this.localStorageService.hasPermission(1132) &&
            !this.localStorageService.hasPermission(1652);
        this.autocompleteStoragePlace();
    }

    ngAfterViewInit() {}

    ngOnDestroy() {
        this.revokePdfUrl();
    }

    setScanKind(kind: 'lieferschein' | 'rechnung') {
        this.scanKind = kind;
        this.scanKindLocked = true;
    }

    get scanKindLabel(): string {
        if (this.scanKind === 'rechnung') {
            return 'Rechnung';
        }
        if (this.scanKind === 'gutschrift') {
            return 'Gutschrift';
        }
        return 'Lieferschein';
    }

    get scanWarnings(): string[] {
        return Array.isArray(this.draft?.warnings) ? this.draft.warnings : [];
    }

    get ocrFailed(): boolean {
        const src = String(this.draft?.extract?.source || '').toLowerCase();
        return src === 'stub' || src.includes('extract-failed') || this.ocrHadNoLines;
    }

    copyScanJson(): void {
        if (!this.draft) {
            return;
        }

        const json = JSON.stringify(this.buildScanDebugJson(), null, 2);
        if (this.clipboard.copy(json)) {
            this.notification.infoMessage('JSON kopiert – kannst du hier einfügen.');
        } else {
            this.notification.infoMessage('Kopieren nicht möglich – bitte nochmal versuchen.');
        }
    }

    private buildScanDebugJson(): Record<string, unknown> {
        return {
            file: this.scanFile?.name || this.draft.originalFileName || null,
            runId: this.draft.runId,
            documentKind: this.draft.documentKind || this.scanKind,
            suggestedLieferantName: this.draft.suggestedLieferantName,
            suggestedLieferantId: this.draft.suggestedLieferantId,
            suggestedRechnungNr: this.draft.suggestedRechnungNr,
            suggestedDatum: this.draft.suggestedDatum,
            suggestedBestellungExternId: this.draft.suggestedBestellungExternId,
            warnings: this.draft.warnings || [],
            extract: this.draft.extract || null,
            positionen: this.draft.positionen || [],
            scanLines: this.scanLines.map(l => ({
                artikel: l.artikel,
                artikelId: l.artikelId,
                menge: l.menge,
                mengeSoll: l.mengeSoll,
                charge: l.charge,
                mhd: this.scanLineMhdIso(l.mhd),
                matched: l.matched,
                bestellNr: l.bestellNr,
                gehtZurueck: l.gehtZurueck,
                verbrauch: l.verbrauch,
            })),
        };
    }

    private scanLineMhdIso(mhd: Date | null): string | null {
        if (!(mhd instanceof Date) || isNaN(mhd.getTime())) {
            return null;
        }
        const y = mhd.getFullYear();
        const m = String(mhd.getMonth() + 1).padStart(2, '0');
        const d = String(mhd.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    get scanOrderHint(): string {
        const matchedArticles = this.scanLines.some(l => l.artikelId > 0);
        if (this.scanOrders.length > 1) {
            return this.ocrHadNoLines
                ? 'Keine Positionen auf dem Beleg erkannt – bitte alle zutreffenden Bestellungen anwählen.'
                : 'Nur Bestellungen mit den zugeordneten Artikeln. Eine Lieferung kann mehrere Bestellungen enthalten.';
        }
        if (this.scanOrders.length) {
            return this.ocrHadNoLines
                ? 'Keine Positionen auf dem Beleg erkannt – offene Bestellung dieses Lieferanten:'
                : '';
        }
        if (!matchedArticles && !this.ocrHadNoLines) {
            return 'Artikel zuordnen, dann erscheinen die passenden offenen Bestellungen.';
        }
        if (this.draft?.suggestedLieferantId > 0 || this.draft?.suggestedLieferantName) {
            return matchedArticles
                ? 'Keine offene Bestellung zu den zugeordneten Artikeln.'
                : 'Keine offene Bestellung zu diesem Lieferanten.';
        }
        return this.ocrFailed
            ? 'Beleg konnte nicht gelesen werden – Bestellung und Artikel bitte manuell wählen.'
            : 'Keine offene Bestellung zu den erkannten Artikeln.';
    }

    get includedScanLines(): WareneingangScanLine[] {
        return this.scanLines.filter(l => l.included);
    }

    get orderProgress(): OrderProgressView[] {
        return this.buildOrderProgress();
    }

    get extraScanLines(): WareneingangScanLine[] {
        const keys = new Set<string>();
        for (const order of this.scanSelectedOrders) {
            for (const pos of order?.positionen || []) {
                const artikelId = Number(pos.artikel_id) || 0;
                const orderId = Number(order.bestellung_extern_id) || Number(pos.bestellung_extern_id) || 0;
                if (orderId > 0 && artikelId > 0) {
                    keys.add(this.orderArtikelKey(orderId, artikelId));
                }
            }
        }
        return this.includedScanLines.filter(line => {
            if (line.gehtZurueck) {
                return false;
            }
            if (!(line.artikelId > 0) || !line.bestellungExternId) {
                return line.matched === true;
            }
            return !keys.has(this.orderArtikelKey(line.bestellungExternId, line.artikelId));
        });
    }

    get canSaveScan(): boolean {
        if (this.scanning || this.scanSaving || this.scanFailed) {
            return false;
        }
        const lines = this.includedScanLines;
        if (lines.length === 0) {
            return false;
        }
        return lines.every(
            l =>
                l.artikelId > 0 &&
                l.menge > 0 &&
                Number(l.lagerplatz?.lagerplatz_id) > 0 &&
                (!l.chargenpflichtig || (!!l.charge && !!l.mhd)),
        );
    }

    get manualNeedsLot(): boolean {
        return KieneUtils.isApiOne(this.newMaterial?.charge);
    }

    onPdfDropped(files: FileList | File[]) {
        const file = Array.from(files || [])[0];
        if (file) {
            this.startScan(file);
        }
    }

    onPdfPicked(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input?.files?.[0];
        if (file) {
            this.startScan(file);
        }
        if (input) {
            input.value = '';
        }
    }

    togglePdfPanel() {
        this.pdfPanelOpen = !this.pdfPanelOpen;
    }

    openPdfPicker() {
        this.pdfPanelOpen = true;
        this.pdfPicker?.nativeElement?.click();
    }

    clearScan() {
        this.revokePdfUrl();
        this.scanFile = null;
        this.scanning = false;
        this.scanFailed = false;
        this.scanError = '';
        this.draft = null;
        this.scanLines = [];
        this.scanOrders = [];
        this.scanSelectedOrders = [];
        this.scanOrdersLoading = false;
        this.ocrHadNoLines = false;
        this.scanLieferant = null;
        this.pdfSrc = null;
        this.pdfPage = 1;
        this.pdfPages = 0;
        this.scanInfoOpen = false;
        this.scanKind = 'lieferschein';
        this.scanKindLocked = false;
        this.selectedOrder = new BestellungExtern();
        this.selectedStoragePlace = new StoragePlace();
        this.storagePlaceSuggestions = [];
    }

    prevPdfPage() {
        if (this.pdfPage > 1) {
            this.pdfPage--;
        }
    }

    nextPdfPage() {
        if (this.pdfPages && this.pdfPage < this.pdfPages) {
            this.pdfPage++;
        }
    }

    onPdfLoad(pdf: { numPages?: number }) {
        this.pdfPages = pdf?.numPages ?? 0;
        this.pdfLoading = false;
        if (this.pdfPages >= 2 && this.pdfPage === 1) {
            this.pdfPage = 2;
        }
    }

    toggleScanOrder(order: BestellungExtern) {
        if (!order?.bestellung_extern_id) {
            return;
        }
        if (this.isScanOrderSelected(order)) {
            this.setSelectedScanOrders(
                this.scanSelectedOrders.filter(
                    o => !KieneUtils.sameId(o.bestellung_extern_id, order.bestellung_extern_id),
                ),
            );
        } else {
            this.setSelectedScanOrders([...this.scanSelectedOrders, order]);
        }
    }

    isScanOrderSelected(order: BestellungExtern) {
        return this.scanSelectedOrders.some(o =>
            KieneUtils.sameId(o.bestellung_extern_id, order.bestellung_extern_id),
        );
    }

    onLineOrderChange(line: WareneingangScanLine) {
        const opt = (line.orderOptions || []).find(o =>
            KieneUtils.sameId(o.bestellung_extern_id, line.bestellungExternId),
        );
        line.bestellNr = opt?.bestell_nr ?? null;
        line.mengeSoll = opt?.mengeSoll ?? null;
    }

    setScanLieferant(lieferant: { lieferant_id?: number; bezeichnung?: string }) {
        if (!(Number(lieferant?.lieferant_id) > 0)) {
            return;
        }
        this.scanLieferant = {
            lieferant_id: Number(lieferant.lieferant_id),
            bezeichnung: String(lieferant.bezeichnung || ''),
        };
        if (this.draft) {
            this.draft.suggestedLieferantId = this.scanLieferant.lieferant_id;
            this.draft.suggestedLieferantName = this.scanLieferant.bezeichnung;
            this.draft.suggestedBestellungExternId = null;
        }
        this.scanSelectedOrders = [];
        this.scanOrders = [];
        this.selectedOrder = new BestellungExtern();
        if (this.ocrHadNoLines) {
            this.scanLines = [];
        }
        this.storagePlaceSuggestions = [];
        this.loadScanOrders();
    }

    setScanArticle(line: WareneingangScanLine, material: Material) {
        if (!material) {
            return;
        }
        line.artikelId = material.artikel_id;
        line.artikel = material.artikel_me || material.bezeichnung;
        line.matched = true;
        line.chargenpflichtig = this.articleNeedsLot(material.charge);
        line.verbrauch =
            material.verbrauch != null || material.nicht_abgebbar != null
                ? isVerbrauchArticle(material)
                : null;
        if (!line.chargenpflichtig) {
            line.charge = '';
            line.mhd = null;
        }
        this.reloadLineStoragePlaces(line);
        this.loadScanOrders();
        this.hydrateScanLineVerbrauch();
    }

    onScanLineScanned(line: WareneingangScanLine, event: ArticleScanSelectedEvent) {
        if (!event?.article) {
            this.notification.infoMessage('Kein Artikel im System zu diesem Code.');
            return;
        }
        this.setScanArticle(line, event.article);
        if (event.charge) {
            line.charge = event.charge;
        }
        if (event.mhd) {
            line.mhd = event.mhd;
        }
    }

    addScanLine() {
        this.scanLines = [...this.scanLines, this.emptyScanLine()];
    }

    removeScanLine(line: WareneingangScanLine) {
        this.scanLines = this.scanLines.filter(l => l !== line);
        this.loadScanOrders();
    }

    isOcrJunkName(name: string): boolean {
        const t = (name || '')
            .toLowerCase()
            .replace(/[^a-zäöüß]+/g, ' ')
            .trim();
        if (!t) {
            return true;
        }
        if (/(fenbendatat|tullavis|tulavis|kanüle|kanuele|vitamin|bovalto|respi|suigonan|henke|stulldiarsan|jecuplex|veyfo|nterkokask|ubac|hiprabovis|startvac|calcium|capill)/.test(t)) {
            return false;
        }
        if (/(chargennr|ablaufdatum|folder|transo|kommission|versand|gepackt|kontrolle|gewicht|sgesamt|preisliste|haftnotiz|iban|datenschutz|pflichtinformation|kreissparkasse|auftrag|gefahrgut|nettomasse|lieferpauschale|kohler|amtsgericht|geschaeftsfuehrer|geschaeftsführer|hipwl|referenzbeleg|packstuck|lagerrest|bei bedarf|restmenge)/.test(t)) {
            return true;
        }
        const words = t.split(' ').filter(w => w.length >= 2);
        const long = words.filter(w => w.length >= 5);
        const short = words.filter(w => w.length <= 2);
        if (long.length === 0) {
            return true;
        }
        return words.length >= 6 && short.length / words.length >= 0.5;
    }

    private emptyScanLine(): WareneingangScanLine {
        return {
            artikelId: 0,
            artikel: '',
            menge: 1,
            mengeSoll: null,
            charge: '',
            mhd: null,
            included: true,
            matched: false,
            chargenpflichtig: true,
            bestellungExternId: null,
            bestellNr: null,
            orderOptions: [],
            gehtZurueck: false,
            verbrauch: null,
            lagerplatz: null,
            lagerplatzSuggestions: [],
        };
    }

    private hydrateScanLineVerbrauch() {
        const ids = [
            ...new Set(
                this.scanLines
                    .filter(line => line.artikelId > 0 && line.verbrauch == null)
                    .map(line => line.artikelId),
            ),
        ];
        if (!ids.length) {
            return;
        }
        forkJoin(
            ids.map(id =>
                this.materialService.getOne(id).pipe(
                    catchError(() => of(null)),
                ),
            ),
        ).subscribe({
            next: materials => {
                const byId = new Map<number, Material>();
                for (const material of materials) {
                    if (material?.artikel_id) {
                        byId.set(Number(material.artikel_id), material);
                    }
                }
                for (const line of this.scanLines) {
                    if (line.verbrauch != null) {
                        continue;
                    }
                    const material = byId.get(line.artikelId);
                    if (material) {
                        line.verbrauch = isVerbrauchArticle(material);
                    }
                }
            },
        });
    }

    scanLineMaterial(line: WareneingangScanLine): Material {
        if (!(line?.artikelId > 0)) {
            return null;
        }
        const material = new Material();
        material.artikel_id = line.artikelId;
        material.artikel_me = line.artikel;
        material.bezeichnung = line.artikel;
        return material;
    }

    saveScanReceipt() {
        if (!this.canSaveScan) {
            return;
        }
        const over: string[] = [];
        for (const order of this.orderProgress) {
            for (const p of order.positions) {
                if (p.state === 'over') {
                    over.push(
                        `${p.artikel}: ${this.formatQty(p.ist + p.we)} statt Soll ${this.formatQty(p.soll)}`,
                    );
                }
            }
        }
        if (
            over.length &&
            !window.confirm(
                'Mehr als bestellt auf die Bestellung buchen (Überlieferung / Naturalrabatt)?\n\n' +
                    over.join('\n'),
            )
        ) {
            return;
        }
        this.scanSaving = true;
        const jobs = this.includedScanLines.map(line => {
            const receipt = new GoodReceipt();
            receipt.artikel_id = line.artikelId;
            if (line.chargenpflichtig) {
                receipt.charge = line.charge;
                receipt.mhd = this.formatMhdForSave(line.mhd);
            } else {
                this.applyNoLotDefaults(receipt);
            }
            receipt.menge = line.menge;
            receipt.lagerplatz_id_ziel = line.lagerplatz?.lagerplatz_id;
            if (line.bestellungExternId) {
                receipt.bestellung_extern_id = line.bestellungExternId;
            }
            return receipt;
        });

        from(jobs)
            .pipe(concatMap(receipt => this.stockService.createStock(receipt)), toArray())
            .subscribe({
                next: () => this.attachScanDokument(),
                error: err => {
                    this.scanSaving = false;
                    this.notification.errorMessage(
                        'Ein Teil des Wareneingangs wurde ggf. schon gebucht. Bitte Historie prüfen.',
                        err,
                    );
                },
            });
    }

    isArticleInDatabase(event: ArticleScanSelectedEvent) {
        if (event.article) {
            this.materialSelected(event);
        } else {
            this.openAddArticleDialog();
        }
    }

    materialSelected(event: ArticleScanSelectedEvent) {
        this.newMaterial = event.article;
        this.goodReceipt.artikel_id = event.article.artikel_id;
        this.manualVerbrauch = isVerbrauchArticle(event.article);
        if (event.article.verbrauch == null && event.article.nicht_abgebbar == null) {
            this.materialService.getOne(event.article.artikel_id).subscribe({
                next: m => {
                    this.manualVerbrauch = isVerbrauchArticle(m);
                },
            });
        }
        if (this.articleNeedsLot(event.article.charge)) {
            if (event.mhd) {
                this.goodReceipt.mhd = event.mhd;
            }
            if (event.charge) {
                this.goodReceipt.charge = event.charge;
            }
        } else {
            this.applyNoLotDefaults(this.goodReceipt);
        }
        this.syncLagerplatzAuswahl();
        this.loadPossibleOrders(this.newMaterial.artikel_id);
    }

    autocompleteStoragePlace() {
        this.storagePlaceFormCtrl = new UntypedFormControl();
        this.storagePlaces = this.storagePlaceFormCtrl.valueChanges.pipe(
            startWith(''),
            switchMap(value => this.searchStoragePlaces(value)),
        );
    }

    searchStoragePlaces(value: string) {
        return this.storagePlaceService.searchStoragePlaces(value).pipe(map(response => response.records));
    }

    getStoragePlaceSuggestions(artikelId: number) {
        if (artikelId) {
            this.storagePlaceService.getStoragePlacesForMaterial(artikelId).subscribe({
                next: response => {
                    this.storagePlaceSuggestions = response.records;
                },
                error: error => {
                    this.notification.errorMessage('Fehler!', error);
                },
            });
        }
    }

    setLineStoragePlace(line: WareneingangScanLine, storagePlace: StoragePlace) {
        if (!storagePlace?.lagerplatz_id) {
            return;
        }
        line.lagerplatz = storagePlace;
    }

    isLineStorageSelected(line: WareneingangScanLine, storagePlace: StoragePlace) {
        return KieneUtils.sameId(line.lagerplatz?.lagerplatz_id, storagePlace.lagerplatz_id);
    }

    setSelectedStoragePlace(storagePlace: StoragePlace) {
        if (KieneUtils.sameId(this.selectedStoragePlace.lagerplatz_id, storagePlace.lagerplatz_id)) {
            this.selectedStoragePlace = new StoragePlace();
        } else {
            this.selectedStoragePlace = storagePlace;
        }
    }

    isStoragePlaceSelected(storagePlace: StoragePlace) {
        return KieneUtils.sameId(this.selectedStoragePlace.lagerplatz_id, storagePlace.lagerplatz_id);
    }

    isOrderSelected(po: BestellungExtern) {
        return KieneUtils.sameId(this.selectedOrder.bestellung_extern_id, po.bestellung_extern_id);
    }

    setSelectedOrder(po: BestellungExtern) {
        this.selectedOrder = po;
        this.syncLagerplatzAuswahl();
    }

    nurLaborLagerplatz(): boolean {
        if (isEinkaufLaborArticle(this.newMaterial)) {
            return true;
        }
        return this.istLaborWarenbestellung(this.selectedOrder);
    }

    scanOrderForLine(line: WareneingangScanLine): BestellungExtern | undefined {
        return [...this.scanSelectedOrders, ...this.scanOrders].find(o =>
            KieneUtils.sameId(o.bestellung_extern_id, line.bestellungExternId),
        );
    }

    istLaborWarenbestellung(order: BestellungExtern | null | undefined): boolean {
        if (!order?.bestellung_extern_id || Number(order.untersuchungsauftrag_id) > 0) {
            return false;
        }
        const positionen = order.positionen || [];
        return positionen.length > 0 && positionen.every(p => Number(p.laborartikel) > 0 || isEinkaufLaborArticle(p));
    }

    private syncLagerplatzAuswahl() {
        if (!this.nurLaborLagerplatz()) {
            if (this.newMaterial?.artikel_id) {
                this.getStoragePlaceSuggestions(this.newMaterial.artikel_id);
            }
            return;
        }
        this.showStoragePlaceAutocomplete = false;
        this.storagePlaceService.getLaborStoragePlaces().subscribe({
            next: response => {
                this.storagePlaceSuggestions = response.records || [];
                const labor =
                    this.storagePlaceSuggestions.find(p => String(p.bezeichnung || '').trim().toLowerCase() === 'labor') ||
                    this.storagePlaceSuggestions[0];
                if (labor) {
                    this.selectedStoragePlace = labor;
                }
            },
        });
    }

    loadPossibleOrders(materialId: number) {
        this.bestellungExternService.getBestellungenByMaterialAndStatus(materialId, OPEN_ORDER_STATUS).subscribe(response => {
            this.possibleBestellungen = response.records;
        });
    }

    saveGoodReceipt() {
        this.saveButtonDisabled = true;
        if (!this.manualNeedsLot) {
            this.applyNoLotDefaults(this.goodReceipt);
        } else if (this.goodReceipt.mhd) {
            this.goodReceipt.mhd = this.formatMhdForSave(this.goodReceipt.mhd);
        }
        this.goodReceipt.lagerplatz_id_ziel = this.selectedStoragePlace.lagerplatz_id;
        this.goodReceipt.bestellung_extern_id = this.selectedOrder.bestellung_extern_id;
        this.stockService.createStock(this.goodReceipt).subscribe(
            () => {
                this.notification.infoMessage('Der neue Bestand wurde erfolgreich gespeichert!');
                this.clearInputs();
                this.saveButtonDisabled = false;
            },
            error => {
                this.notification.errorMessage(
                    'Ein Fehler ist aufgetreten! Der neue Bestand konnte nicht gespeichert werden!',
                    error,
                );
                this.saveButtonDisabled = false;
            },
        );
    }

    clearInputs() {
        this.newMaterial = undefined;
        this.manualVerbrauch = null;
        this.storagePlaceFormCtrl.setValue(undefined);
        this.storagePlaceSuggestions = [];
        this.selectedOrder = new BestellungExtern();
        this.goodReceipt = new GoodReceipt();
        this.selectedStoragePlace = new StoragePlace();
    }

    setProduktkennzeichnung(pk: string) {
        this.produktkennzeichnung = pk;
    }

    openAddArticleDialog() {
        const pk = this.produktkennzeichnung || this.materialAutocomplete.getValue();
        if (!pk) {
            this.notification.infoMessage('Keine alternative Produktbezeichnung eingetragen!');
        } else {
            this.dialog
                .open(GoodsReceiptAddArticleComponent, { data: pk, minWidth: '400px' })
                .afterClosed()
                .subscribe({
                    next: (artikel: Material) => {
                        if (artikel) {
                            const event = new ArticleScanSelectedEvent();
                            event.article = artikel;
                            this.materialSelected(event);
                        }
                    },
                });
        }
    }

    reload() {
        this.clearInputs();
        this.clearScan();
    }

    private startScan(file: File) {
        this.clearScan();
        this.pdfPanelOpen = true;
        this.scanFile = file;
        this.scanning = true;
        this.initPdfViewer(file);
        this.invoiceKi
            .preview(file, { persistTarget: 'bestellung_extern' })
            .subscribe({
                next: draft => {
                    this.scanning = false;
                    this.draft = draft;
                    if (!this.scanKindLocked) {
                        const kind = (draft?.documentKind || 'lieferschein').toLowerCase();
                        this.scanKind =
                            kind === 'rechnung' || kind === 'invoice'
                                ? 'rechnung'
                                : kind === 'gutschrift'
                                  ? 'gutschrift'
                                  : 'lieferschein';
                    }
                    this.scanLines = this.dedupeScanLines(this.buildScanLines(draft));
                    this.ocrHadNoLines = this.scanLines.length === 0;
                    this.scanLieferant = this.lieferantFromDraft(draft);
                    this.reloadStorageSuggestions();
                    this.loadScanOrders();
                    this.hydrateScanLineVerbrauch();
                },
                error: err => {
                    this.scanning = false;
                    this.scanFailed = true;
                    this.scanError =
                        err?.error?.errortext ||
                        err?.error?.message ||
                        err?.message ||
                        'Beleg konnte nicht gelesen werden.';
                },
            });
    }

    private lieferantFromDraft(draft: WawiInvoiceDraftDto): { lieferant_id: number; bezeichnung: string } {
        const id = Number(draft?.suggestedLieferantId) || 0;
        const name = String(draft?.suggestedLieferantName || '').trim();
        if (!(id > 0) && !name) {
            return null;
        }
        return { lieferant_id: id, bezeichnung: name };
    }

    private buildScanLines(draft: WawiInvoiceDraftDto): WareneingangScanLine[] {
        const raw = Array.isArray(draft?.positionen) && draft.positionen.length > 0
            ? draft.positionen
            : (draft?.extract?.lines || []).map(l => ({
                  artikelId: l.artikelId,
                  bezeichnung: l.description,
                  artikelMe: l.artikelMe,
                  menge: l.menge,
                  charge: l.charge,
                  mhd: l.mhd,
                  chargenpflicht: l.chargenpflicht,
                  gehtZurueck: l.gehtZurueck,
              }));
        const lines: WareneingangScanLine[] = [];
        for (const item of raw) {
            const row = item as Record<string, unknown>;
            const bezeichnung = String(row['bezeichnung'] || row['description'] || '').trim();
            if (this.isPlaceholderScanLine(bezeichnung) || this.isHeaderScanLine(bezeichnung)) {
                continue;
            }
            const artikelId = Number(row['artikelId']) || 0;
            if (!(artikelId > 0) && this.isOcrJunkName(bezeichnung)) {
                continue;
            }
            const mengeRaw = row['menge'];
            const menge = mengeRaw != null && mengeRaw !== '' ? Number(mengeRaw) : 0;
            if (!bezeichnung && !(artikelId > 0) && !(menge > 0)) {
                continue;
            }
            const blob = bezeichnung;
            const charge = String(row['charge'] || this.parseCharge(blob) || '');
            const mhd = this.parseMhdValue(row['mhd']) || this.parseMhd(blob);
            const gehtZurueck =
                row['gehtZurueck'] === true || this.isReturnedScanLine(bezeichnung);
            const matched = artikelId > 0;
            lines.push({
                artikelId,
                artikel: String(row['artikelMe'] || bezeichnung),
                menge: menge > 0 ? menge : 0,
                mengeSoll: null,
                charge,
                mhd,
                included: !gehtZurueck,
                matched,
                chargenpflichtig: this.isChargenpflichtig(row),
                bestellungExternId: null,
                bestellNr: null,
                orderOptions: [],
                gehtZurueck,
                verbrauch: null,
                lagerplatz: null,
                lagerplatzSuggestions: [],
            });
        }
        return lines;
    }

    private dedupeScanLines(lines: WareneingangScanLine[]): WareneingangScanLine[] {
        const result: WareneingangScanLine[] = [];
        for (const line of lines) {
            const idx = result.findIndex(
                x =>
                    x.artikelId > 0 &&
                    line.artikelId > 0 &&
                    KieneUtils.sameId(x.artikelId, line.artikelId) &&
                    Number(x.menge) === Number(line.menge) &&
                    Number(line.menge) > 0 &&
                    !x.gehtZurueck &&
                    !line.gehtZurueck &&
                    (!x.charge || !line.charge || x.charge === line.charge),
            );
            if (idx < 0) {
                result.push(line);
                continue;
            }
            if (!result[idx].charge && line.charge) {
                result[idx] = {
                    ...result[idx],
                    charge: line.charge,
                    mhd: line.mhd || result[idx].mhd,
                };
            }
        }
        return this.dropDateFragmentScanLines(result);
    }

    /** MHD-Tag als Menge (31.10.2027 → 31) neben derselben Charge mit echter Liefermenge. */
    private dropDateFragmentScanLines(lines: WareneingangScanLine[]): WareneingangScanLine[] {
        return lines.filter(line => {
            const qty = Number(line.menge) || 0;
            const charge = (line.charge || '').trim();
            if (qty < 1 || qty > 31 || !charge || line.gehtZurueck) {
                return true;
            }
            const sibling = lines.find(
                x =>
                    x !== line &&
                    !x.gehtZurueck &&
                    (x.charge || '').trim().toLowerCase() === charge.toLowerCase() &&
                    (Number(x.menge) || 0) >= 80 &&
                    (line.artikelId <= 0 ||
                        x.artikelId <= 0 ||
                        KieneUtils.sameId(x.artikelId, line.artikelId)),
            );
            if (!sibling) {
                return true;
            }
            if (!sibling.mhd && line.mhd) {
                sibling.mhd = line.mhd;
            }
            return false;
        });
    }

    private isReturnedScanLine(bezeichnung: string): boolean {
        const d = (bezeichnung || '').toLowerCase();
        return (
            d.includes('geht zurück') ||
            d.includes('geht zurueck') ||
            d.includes('wieder zurück') ||
            d.includes('retoure')
        );
    }

    private isHeaderScanLine(bezeichnung: string): boolean {
        const raw = (bezeichnung || '').trim();
        if (!raw) {
            return false;
        }
        const stripped = raw
            .toLowerCase()
            .replace(
                /\b(laterial|material|charge|chargen|chargennr|ablaufdatum|verfall\w*|vertall\w*|menge|artikel|bezeichnung|pos\.?|me|mhd|einheit)\b/g,
                ' ',
            )
            .replace(/[^a-zäöüß0-9]+/g, ' ')
            .trim();
        return stripped.length < 6;
    }

    private isPlaceholderScanLine(bezeichnung: string): boolean {
        const d = (bezeichnung || '').trim().toLowerCase();
        return (
            d.startsWith('manuell prüfen') ||
            d.includes('extract ohne positionen') ||
            d.includes('extract fehlgeschlagen') ||
            d.includes('extract ohne betrag')
        );
    }

    /** Artikel-Einstellung „Charge J/N“ – ohne Charge auch kein MHD. */
    private articleNeedsLot(chargeFlag: unknown): boolean {
        return KieneUtils.isApiOne(chargeFlag);
    }

    private applyNoLotDefaults(receipt: GoodReceipt) {
        receipt.charge = '-';
        receipt.mhd = NO_EXPIRY;
    }

    private formatMhdForSave(value: Date | string | null): string {
        if (value == null || value === '') {
            return NO_EXPIRY;
        }
        if (typeof value === 'string') {
            return value.length >= 10 ? value.slice(0, 10) : value;
        }
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, '0');
        const d = String(value.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    private isChargenpflichtig(row: Record<string, unknown>): boolean {
        return this.articleNeedsLot(row['chargenpflicht'] ?? row['chargenpflichtig']);
    }

    private matchedArtikelIds(): number[] {
        return [
            ...new Set(this.scanLines.filter(l => l.included).map(l => l.artikelId).filter(id => id > 0)),
        ];
    }

    private loadScanOrders() {
        const artikelIds = this.matchedArtikelIds();
        if (artikelIds.length === 0) {
            if (this.ocrHadNoLines) {
                this.loadScanOrdersByLieferant();
            } else {
                this.loadSuggestedOrderOnly();
            }
            return;
        }
        const previousIds = this.scanSelectedOrders.map(o => Number(o.bestellung_extern_id)).filter(id => id > 0);
        forkJoin(
            artikelIds.map(id =>
                this.bestellungExternService.getBestellungenByMaterialAndStatus(id, OPEN_ORDER_STATUS).pipe(
                    map(res => res.records || []),
                    catchError(() => of([] as BestellungExtern[])),
                ),
            ),
        ).subscribe(lists => {
            const counts = new Map<number, { order: BestellungExtern; hits: number }>();
            for (const list of lists) {
                for (const order of list) {
                    const id = order.bestellung_extern_id;
                    const prev = counts.get(id);
                    if (prev) {
                        prev.hits++;
                    } else {
                        counts.set(id, { order, hits: 1 });
                    }
                }
            }
            const lieferantId = this.draft?.suggestedLieferantId;
            const matching = [...counts.values()]
                .filter(r => !lieferantId || KieneUtils.sameId(r.order.lieferant_id, lieferantId))
                .sort((a, b) => b.hits - a.hits)
                .map(r => r.order);
            this.scanOrders = this.sortScanOrders(this.mergeOrders(matching));
            const articlesOf = (orderId: number) =>
                artikelIds.filter((_, i) =>
                    (lists[i] || []).some(o => KieneUtils.sameId(o.bestellung_extern_id, orderId)),
                );
            const remaining = new Set(artikelIds);
            const auto: BestellungExtern[] = [];
            const take = (order: BestellungExtern) => {
                if (!order?.bestellung_extern_id) {
                    return;
                }
                if (auto.some(o => KieneUtils.sameId(o.bestellung_extern_id, order.bestellung_extern_id))) {
                    return;
                }
                auto.push(order);
                for (const artikelId of articlesOf(order.bestellung_extern_id)) {
                    remaining.delete(artikelId);
                }
            };
            const stillSelected = this.scanOrders.filter(o =>
                previousIds.some(id => KieneUtils.sameId(o.bestellung_extern_id, id)),
            );
            if (stillSelected.length) {
                stillSelected.forEach(take);
            }
            const suggestedId = this.draft?.suggestedBestellungExternId;
            const suggested = suggestedId
                ? this.scanOrders.find(o => KieneUtils.sameId(o.bestellung_extern_id, suggestedId))
                : null;
            if (suggested) {
                take(suggested);
            }
            while (remaining.size > 0) {
                let best: BestellungExtern = null;
                let bestHits = 0;
                for (const order of this.scanOrders) {
                    if (auto.some(o => KieneUtils.sameId(o.bestellung_extern_id, order.bestellung_extern_id))) {
                        continue;
                    }
                    const hits = articlesOf(order.bestellung_extern_id).filter(id => remaining.has(id)).length;
                    if (hits > bestHits) {
                        best = order;
                        bestHits = hits;
                    }
                }
                if (!best) {
                    break;
                }
                take(best);
            }
            if (auto.length === 0 && this.scanOrders.length === 1) {
                take(this.scanOrders[0]);
            }
            this.setSelectedScanOrders(auto);
        });
    }

    private loadSuggestedOrderOnly() {
        const suggestedId = Number(this.draft?.suggestedBestellungExternId) || 0;
        if (!(suggestedId > 0)) {
            this.scanOrders = [];
            this.setSelectedScanOrders([]);
            return;
        }
        const params = new HttpParams().set('bestellung_extern_id', String(suggestedId));
        this.api.get(this.apiBaseUrl + 'bestellung_extern/read_one.php', params).subscribe({
            next: (be: BestellungExtern) => {
                if (!be?.bestellung_extern_id) {
                    this.scanOrders = [];
                    this.setSelectedScanOrders([]);
                    return;
                }
                this.scanOrders = [be];
                this.setSelectedScanOrders([be]);
            },
            error: () => {
                this.scanOrders = [];
                this.setSelectedScanOrders([]);
            },
        });
    }

    private loadScanOrdersByLieferant() {
        const lieferantId = Number(this.draft?.suggestedLieferantId) || 0;
        const suggestedId = Number(this.draft?.suggestedBestellungExternId) || 0;
        if (!(lieferantId > 0) && !(suggestedId > 0)) {
            this.scanOrders = [];
            this.setSelectedScanOrders([]);
            return;
        }
        if (!(lieferantId > 0)) {
            const params = new HttpParams().set('bestellung_extern_id', String(suggestedId));
            this.api.get(this.apiBaseUrl + 'bestellung_extern/read_one.php', params).subscribe({
                next: (be: BestellungExtern) => {
                    if (!be?.bestellung_extern_id) {
                        this.scanOrders = [];
                        this.setSelectedScanOrders([]);
                        return;
                    }
                    this.scanOrders = [be];
                    this.setSelectedScanOrders([be]);
                },
                error: () => {
                    this.scanOrders = [];
                    this.setSelectedScanOrders([]);
                },
            });
            return;
        }
        this.bestellungExternService.getOpenOrdersByLieferant(lieferantId, OPEN_ORDER_STATUS).subscribe({
            next: res => {
                this.scanOrders = this.sortScanOrders(res?.records || []);
                const suggested = suggestedId
                    ? this.scanOrders.find(o => KieneUtils.sameId(o.bestellung_extern_id, suggestedId))
                    : null;
                if (suggested) {
                    this.setSelectedScanOrders([suggested]);
                } else if (this.scanOrders.length === 1) {
                    this.setSelectedScanOrders([this.scanOrders[0]]);
                } else {
                    this.setSelectedScanOrders([]);
                }
            },
            error: () => {
                this.scanOrders = [];
                this.setSelectedScanOrders([]);
            },
        });
    }

    private setSelectedScanOrders(orders: BestellungExtern[]) {
        this.scanSelectedOrders = (orders || []).filter(o => Number(o?.bestellung_extern_id) > 0);
        this.selectedOrder = this.scanSelectedOrders[0] || new BestellungExtern();
        this.applySelectedOrders();
    }

    scanOrderLabel(po: BestellungExtern): string {
        const year = this.orderYear(po);
        return year ? `${po.bestell_nr} (${year})` : String(po.bestell_nr ?? '');
    }

    private orderYear(po: BestellungExtern): string {
        const raw = po?.bestellt || po?.erstellt;
        if (!raw) {
            return '';
        }
        const d = new Date(raw as string | Date);
        return Number.isNaN(d.getTime()) ? '' : String(d.getFullYear());
    }

    private mergeOrders(orders: BestellungExtern[]): BestellungExtern[] {
        const byId = new Map<number, BestellungExtern>();
        for (const order of orders || []) {
            const id = Number(order?.bestellung_extern_id) || 0;
            if (id > 0 && !byId.has(id)) {
                byId.set(id, order);
            }
        }
        return [...byId.values()];
    }

    private sortScanOrders(orders: BestellungExtern[]): BestellungExtern[] {
        return [...(orders || [])].sort((a, b) => {
            const aTime = new Date((a?.bestellt || a?.erstellt) as string | Date).getTime() || 0;
            const bTime = new Date((b?.bestellt || b?.erstellt) as string | Date).getTime() || 0;
            return bTime - aTime;
        });
    }

    private applySelectedOrders() {
        const selected = this.scanSelectedOrders;
        if (selected.length === 0) {
            this.scanOrdersLoading = false;
            this.clearLineOrderAssignment();
            return;
        }
        this.scanOrdersLoading = true;
        const seq = ++this.selectedOrdersLoadSeq;
        forkJoin(
            selected.map(order => {
                const params = new HttpParams().set('bestellung_extern_id', String(order.bestellung_extern_id));
                return this.api.get(this.apiBaseUrl + 'bestellung_extern/read_one.php', params).pipe(
                    catchError(() => of(order)),
                );
            }),
        ).subscribe({
            next: (bes: BestellungExtern[]) => {
                if (seq !== this.selectedOrdersLoadSeq) {
                    return;
                }
                this.scanOrdersLoading = false;
                this.scanSelectedOrders = (bes || []).filter(o => Number(o?.bestellung_extern_id) > 0);
                this.selectedOrder = this.scanSelectedOrders[0] || new BestellungExtern();
                const allPos = this.flattenOrderPositions(this.scanSelectedOrders);
                if (this.ocrHadNoLines) {
                    this.scanLines = allPos.map(pos => this.scanLineFromOrderPosition(pos));
                    this.reloadStorageSuggestions();
                    this.hydrateScanLineVerbrauch();
                    return;
                }
                const idsBefore = this.matchedArtikelIds();
                this.assignLinesToOrders(allPos);
                this.reloadAllLineStoragePlaces();
                if (this.matchedArtikelIds().length > idsBefore.length) {
                    this.loadScanOrders();
                }
            },
            error: () => {
                if (seq !== this.selectedOrdersLoadSeq) {
                    return;
                }
                this.scanOrdersLoading = false;
            },
        });
    }

    formatQty(value: number | null | undefined): string {
        const n = Number(value);
        if (!Number.isFinite(n)) {
            return '–';
        }
        const rounded = Math.round(n * 100) / 100;
        return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace('.', ',');
    }

    isScanLineOverbook(line: WareneingangScanLine): boolean {
        if (!(line?.bestellungExternId > 0) || !(line.artikelId > 0)) {
            return false;
        }
        return this.orderProgress.some(order =>
            KieneUtils.sameId(order.bestellungExternId, line.bestellungExternId) &&
            order.positions.some(
                p => KieneUtils.sameId(p.artikelId, line.artikelId) && p.state === 'over',
            ),
        );
    }

    overbookLabel(pos: OrderProgressPosition): string {
        const extra = pos.ist + pos.we - pos.soll;
        return extra > 0 ? '+' + this.formatQty(extra) : this.formatQty(pos.offen);
    }

    private buildOrderProgress(): OrderProgressView[] {
        const weByKey = new Map<string, number>();
        for (const line of this.includedScanLines) {
            if (line.gehtZurueck || !(line.artikelId > 0) || !line.bestellungExternId) {
                continue;
            }
            const key = this.orderArtikelKey(line.bestellungExternId, line.artikelId);
            weByKey.set(key, (weByKey.get(key) || 0) + (Number(line.menge) || 0));
        }

        return this.scanSelectedOrders.map(order => {
            const orderId = Number(order.bestellung_extern_id) || 0;
            const positions: OrderProgressPosition[] = (order.positionen || []).map(pos => {
                const artikelId = Number(pos.artikel_id) || 0;
                const soll = Number(pos.menge_soll) || 0;
                const ist = Number(pos.menge_ist) || 0;
                const we = weByKey.get(this.orderArtikelKey(orderId, artikelId)) || 0;
                const after = ist + we;
                const offen = soll - after;
                let state: OrderProgressPosition['state'] = 'open';
                if (we > 0 && after > soll && soll > 0) {
                    state = 'over';
                } else if (we > 0 && after >= soll) {
                    state = 'done';
                } else if (we > 0) {
                    state = 'partial';
                } else if (ist >= soll && soll > 0) {
                    state = 'complete';
                }
                return {
                    artikel: String(pos.artikel_me || pos.artikel || 'Artikel'),
                    artikelId,
                    soll,
                    ist,
                    we,
                    offen,
                    state,
                };
            });
            const totalSoll = positions.reduce((sum, p) => sum + p.soll, 0);
            const filled = positions.reduce((sum, p) => sum + Math.min(p.ist + p.we, p.soll), 0);
            const over = positions.some(p => p.state === 'over');
            return {
                bestellungExternId: orderId,
                bestellNr: Number(order.bestell_nr) || 0,
                lieferant: String(order.lieferant || ''),
                bestellt: order.bestellt || order.erstellt || null,
                qtyPercent: totalSoll > 0 ? Math.min(100, (filled / totalSoll) * 100) : 0,
                over,
                hitCount: positions.filter(p => p.we > 0).length,
                positions,
            };
        });
    }

    private clearLineOrderAssignment() {
        if (this.ocrHadNoLines) {
            this.scanLines = [];
            this.storagePlaceSuggestions = [];
            return;
        }
        for (const line of this.scanLines) {
            line.bestellungExternId = null;
            line.bestellNr = null;
            line.mengeSoll = null;
            line.orderOptions = [];
            if (!(Number(line.menge) > 0)) {
                line.menge = 1;
            }
        }
    }

    private flattenOrderPositions(orders: BestellungExtern[]): OrderPositionRef[] {
        const rows: OrderPositionRef[] = [];
        for (const be of orders || []) {
            for (const pos of be?.positionen || []) {
                const soll = Number(pos.menge_soll) || 0;
                const ist = Number(pos.menge_ist) || 0;
                rows.push({
                    bestellung_extern_id: Number(be.bestellung_extern_id) || Number(pos.bestellung_extern_id) || 0,
                    bestell_nr: Number(be.bestell_nr || pos.bestell_nr) || 0,
                    artikel_id: Number(pos.artikel_id) || 0,
                    artikel: String(pos.artikel_me || pos.artikel || 'Artikel'),
                    menge_soll: soll,
                    rest: soll - ist,
                    chargenpflicht: pos.chargenpflicht,
                });
            }
        }
        return rows;
    }

    private scanLineFromOrderPosition(pos: OrderPositionRef): WareneingangScanLine {
        return {
            artikelId: pos.artikel_id,
            artikel: pos.artikel,
            menge: pos.rest > 0 ? pos.rest : pos.menge_soll > 0 ? pos.menge_soll : 1,
            mengeSoll: pos.menge_soll || null,
            charge: '',
            mhd: null,
            included: pos.rest > 0 || pos.menge_soll > 0,
            matched: pos.artikel_id > 0,
            chargenpflichtig: this.articleNeedsLot(pos.chargenpflicht),
            bestellungExternId: pos.bestellung_extern_id || null,
            bestellNr: pos.bestell_nr || null,
            orderOptions: [
                {
                    bestellung_extern_id: pos.bestellung_extern_id,
                    bestell_nr: pos.bestell_nr,
                    mengeSoll: pos.menge_soll || null,
                },
            ],
            gehtZurueck: false,
            verbrauch: null,
            lagerplatz: null,
            lagerplatzSuggestions: [],
        };
    }

    private assignLinesToOrders(allPos: OrderPositionRef[]) {
        const remaining = new Map<string, number>();
        for (const pos of allPos) {
            remaining.set(this.orderArtikelKey(pos.bestellung_extern_id, pos.artikel_id), pos.rest);
        }
        const suggestedId = Number(this.draft?.suggestedBestellungExternId) || 0;
        for (const line of this.scanLines) {
            const options = this.uniqueOrderOptions(
                allPos.filter(
                    p =>
                        (line.artikelId > 0 && KieneUtils.sameId(p.artikel_id, line.artikelId)) ||
                        this.namesLikelyMatch(line.artikel, p.artikel),
                ),
            );
            line.orderOptions = options;
            const preferred =
                (line.bestellungExternId &&
                    allPos.find(
                        p =>
                            KieneUtils.sameId(p.bestellung_extern_id, line.bestellungExternId) &&
                            (line.artikelId > 0
                                ? KieneUtils.sameId(p.artikel_id, line.artikelId)
                                : this.namesLikelyMatch(line.artikel, p.artikel)),
                    )) ||
                this.preferOrderPosition(line, allPos, remaining, suggestedId);
            if (preferred) {
                line.bestellungExternId = preferred.bestellung_extern_id;
                line.bestellNr = preferred.bestell_nr || null;
                line.mengeSoll = preferred.menge_soll || null;
                line.chargenpflichtig =
                    this.articleNeedsLot(preferred.chargenpflicht) || !!(line.charge || '').trim();
                if (!(line.artikelId > 0) && preferred.artikel_id > 0) {
                    line.artikelId = preferred.artikel_id;
                    line.matched = true;
                }
                if (preferred.artikel && KieneUtils.sameId(preferred.artikel_id, line.artikelId)) {
                    line.artikel = preferred.artikel;
                }
                // Fehlende OCR-Menge nicht still mit Bestell-Soll/Rest füllen (Teillieferung!).
                // mengeSoll bleibt als Hinweis; User muss gelieferte Menge bestätigen.
                const key = this.orderArtikelKey(preferred.bestellung_extern_id, preferred.artikel_id);
                remaining.set(key, (remaining.get(key) || 0) - (Number(line.menge) || 0));
            } else {
                line.bestellungExternId = null;
                line.bestellNr = null;
                line.mengeSoll = null;
            }
            // Menge leer lassen, wenn OCR nichts geliefert hat – nicht auf 1 setzen.
        }
    }

    private uniqueOrderOptions(positions: OrderPositionRef[]): ScanLineOrderOption[] {
        const byId = new Map<number, ScanLineOrderOption>();
        for (const pos of positions) {
            if (!(pos.bestellung_extern_id > 0) || byId.has(pos.bestellung_extern_id)) {
                continue;
            }
            byId.set(pos.bestellung_extern_id, {
                bestellung_extern_id: pos.bestellung_extern_id,
                bestell_nr: pos.bestell_nr,
                mengeSoll: pos.menge_soll || null,
            });
        }
        return [...byId.values()];
    }

    private preferOrderPosition(
        line: WareneingangScanLine,
        allPos: OrderPositionRef[],
        remaining: Map<string, number>,
        suggestedId: number,
    ): OrderPositionRef | null {
        const restOf = (p: OrderPositionRef) =>
            remaining.get(this.orderArtikelKey(p.bestellung_extern_id, p.artikel_id)) || 0;
        const pick = (matches: OrderPositionRef[]): OrderPositionRef | null => {
            if (!matches.length) {
                return null;
            }
            const open = matches.filter(p => restOf(p) > 0);
            if (suggestedId) {
                const suggestedOpen = open.find(p =>
                    KieneUtils.sameId(p.bestellung_extern_id, suggestedId),
                );
                if (suggestedOpen) {
                    return suggestedOpen;
                }
            }
            const qty = Number(line.menge) || 0;
            if (qty > 0) {
                const byQty = open.filter(p => p.rest === qty || p.menge_soll === qty);
                if (byQty.length === 1) {
                    return byQty[0];
                }
            }
            if (open.length) {
                return open[0];
            }
            if (suggestedId) {
                const suggested = matches.find(p =>
                    KieneUtils.sameId(p.bestellung_extern_id, suggestedId),
                );
                if (suggested) {
                    return suggested;
                }
            }
            return matches[0];
        };

        if (line.artikelId > 0) {
            return pick(allPos.filter(p => KieneUtils.sameId(p.artikel_id, line.artikelId)));
        }

        return pick(allPos.filter(p => this.namesLikelyMatch(line.artikel, p.artikel)));
    }

    private namesLikelyMatch(ocr: string, stamm: string): boolean {
        const a = (ocr || '').toLowerCase().replace(/[^a-zäöüß0-9]+/gi, ' ').trim();
        const b = (stamm || '').toLowerCase().replace(/[^a-zäöüß0-9]+/gi, ' ').trim();
        if (!a || !b) {
            return false;
        }
        if (a.includes(b) || b.includes(a)) {
            return true;
        }
        const tokensA = a.split(' ').filter(t => t.length >= 5);
        const tokensB = b.split(' ').filter(t => t.length >= 5);
        return tokensA.some(t =>
            tokensB.some(u => t === u || (t.length >= 6 && u.length >= 6 && this.oneEdit(t, u))),
        );
    }

    private oneEdit(a: string, b: string): boolean {
        if (a === b) {
            return true;
        }
        if (Math.abs(a.length - b.length) > 1) {
            return false;
        }
        if (a.length === b.length) {
            let diff = 0;
            for (let i = 0; i < a.length; i++) {
                if (a[i] !== b[i] && ++diff > 1) {
                    return false;
                }
            }
            return diff === 1;
        }
        const shorter = a.length < b.length ? a : b;
        const longer = a.length < b.length ? b : a;
        let i = 0;
        let j = 0;
        let skipped = false;
        while (i < shorter.length && j < longer.length) {
            if (shorter[i] === longer[j]) {
                i++;
                j++;
                continue;
            }
            if (skipped) {
                return false;
            }
            skipped = true;
            j++;
        }
        return i === shorter.length;
    }

    private orderArtikelKey(orderId: number, artikelId: number): string {
        return `${orderId}:${artikelId}`;
    }

    private reloadStorageSuggestions() {
        this.reloadAllLineStoragePlaces();
    }

    private reloadAllLineStoragePlaces() {
        for (const line of this.scanLines) {
            this.reloadLineStoragePlaces(line);
        }
    }

    private reloadLineStoragePlaces(line: WareneingangScanLine) {
        if (!(line.artikelId > 0)) {
            line.lagerplatzSuggestions = [];
            return;
        }
        const nurLabor = this.istLaborWarenbestellung(this.scanOrderForLine(line));
        const load = nurLabor
            ? this.storagePlaceService.getLaborStoragePlaces()
            : this.storagePlaceService.getStoragePlacesForMaterial(line.artikelId);
        load.subscribe({
            next: res => {
                line.lagerplatzSuggestions = res?.records || [];
                const labor = nurLabor
                    ? line.lagerplatzSuggestions.find(p => String(p.bezeichnung || '').trim().toLowerCase() === 'labor') ||
                      line.lagerplatzSuggestions[0]
                    : null;
                if (labor) {
                    line.lagerplatz = labor;
                } else if (!line.lagerplatz?.lagerplatz_id && line.lagerplatzSuggestions.length === 1) {
                    line.lagerplatz = line.lagerplatzSuggestions[0];
                }
            },
            error: () => {
                line.lagerplatzSuggestions = [];
            },
        });
    }

    private attachScanDokument() {
        const orderIds = [
            ...new Set(
                [
                    ...this.scanSelectedOrders.map(o => Number(o.bestellung_extern_id)),
                    ...this.includedScanLines.map(l => Number(l.bestellungExternId)),
                ].filter(id => id > 0),
            ),
        ];
        if (orderIds.length === 0 || !this.scanFile) {
            this.scanSaving = false;
            this.notification.infoMessage('Wareneingang gebucht.');
            this.clearScan();
            return;
        }
        const isRechnung = this.scanKind === 'rechnung';
        const label = isRechnung ? 'Rechnung' : 'Lieferschein';
        const endpoint = isRechnung
            ? 'bestellung_extern_dokument/create_rechnung.php'
            : 'bestellung_extern_dokument/create_lieferschein.php';
        const fd = new FormData();
        fd.append('bestellung_extern_ids', JSON.stringify(orderIds));
        fd.append('dokument', this.scanFile, this.scanFile.name);
        const datum = this.draft?.suggestedDatum || this.formatMhdForSave(new Date());
        fd.append('datum', datum);
        fd.append('skip_invoice_extract', '1');
        const nummer = String(this.draft?.suggestedRechnungNr || '').trim();
        if (nummer) {
            fd.append('nummer', nummer);
        }
        if (isRechnung) {
            const artikelIds = [
                ...new Set(this.includedScanLines.map(l => Number(l.artikelId)).filter(id => id > 0)),
            ];
            if (artikelIds.length) {
                fd.append('artikel_ids', JSON.stringify(artikelIds));
            }
        }
        this.api
            .postFormData(this.apiBaseUrl + endpoint, fd, {
                headers: new HttpHeaders('Phito-Type: fileupload'),
            })
            .subscribe({
                next: () => {
                    this.scanSaving = false;
                    this.notification.infoMessage('Wareneingang gebucht, ' + label + ' hinterlegt.');
                    this.clearScan();
                },
                error: err => {
                    this.scanSaving = false;
                    const status = err?.status;
                    if (status === 0) {
                        this.notification.infoMessage(
                            'Wareneingang gebucht. ' + label + ' bitte unter der Bestellung prüfen.',
                        );
                    } else {
                        this.notification.errorMessage(
                            'Wareneingang gebucht, ' + label + ' konnte nicht hinterlegt werden.',
                            err,
                        );
                    }
                    this.clearScan();
                },
            });
    }

    private parseCharge(text: string): string | null {
        if (!text) {
            return null;
        }
        const m = text.match(
            /(?:charge|chargen(?:nr|nummer)?|ch\.?nr|lot(?:[-.\s]*nr)?|batch)[\s.:/-]*([A-Za-z0-9][A-Za-z0-9._\-\/]{1,40})/i,
        );
        return m?.[1] ?? null;
    }

    private parseMhd(text: string): Date | null {
        if (!text) {
            return null;
        }
        const labeled = text.match(
            /(?:mhd|verfall(?:sdatum)?|verw(?:endbar)?\s*bis|exp(?:iry)?|haltbar(?:keitsdatum)?)[\s.:/-]*(\d{1,4}[./-]\d{1,4}(?:[./-]\d{2,4})?)/i,
        );
        return labeled ? this.parseMhdValue(labeled[1]) : null;
    }

    private parseMhdValue(raw: unknown): Date | null {
        if (raw == null || raw === '') {
            return null;
        }
        if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
            return raw;
        }
        const text = String(raw).trim();
        const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (iso) {
            const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
            return Number.isNaN(d.getTime()) ? null : d;
        }
        const parts = text.split(/[./-]/).map(p => parseInt(p, 10));
        if (parts.length === 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
            const monthFirst = parts[0] <= 12;
            const month = monthFirst ? parts[0] : parts[1];
            const yearRaw = monthFirst ? parts[1] : parts[0];
            const year = yearRaw < 100 ? 2000 + yearRaw : yearRaw;
            if (month < 1 || month > 12) {
                return null;
            }
            return new Date(year, month, 0);
        }
        if (parts.length !== 3 || parts.some(n => Number.isNaN(n))) {
            return null;
        }
        const year = parts[2] < 100 ? 2000 + parts[2] : parts[2];
        const d = new Date(year, parts[1] - 1, parts[0]);
        return Number.isNaN(d.getTime()) ? null : d;
    }

    private initPdfViewer(file: File) {
        this.revokePdfUrl();
        this.pdfLoading = true;
        file.arrayBuffer()
            .then(buf => {
                this.pdfSrc = new Uint8Array(buf);
                this.pdfObjectUrl = URL.createObjectURL(file);
                this.pdfLoading = false;
            })
            .catch(() => {
                this.pdfLoading = false;
                this.pdfSrc = null;
            });
    }

    private revokePdfUrl() {
        if (this.pdfObjectUrl) {
            URL.revokeObjectURL(this.pdfObjectUrl);
            this.pdfObjectUrl = null;
        }
    }
}
